import classNames from "classnames";
import React, { FC, useState } from "react";
import { LuDownload } from "react-icons/lu";

import { AiAskChart } from "../../../slices/AiSlice";
import { exportDataToExcel } from "../../../utils/ExportData";
import { ChartLegend, ReportChart } from "../../reports/custom/ReportChart";
import { formatValue } from "../../reports/custom/reportEngine";

/** A chart under an Ask ProCharted answer (AI reporting, owner 2026-10-25), with a Table view. */
export const AskChart: FC<{ chart: AiAskChart }> = ({ chart }) => {
  const [view, setView] = useState<"chart" | "table">("chart");
  const format = (v: number) => formatValue(chart.unit, v);
  const points = chart.points.map((p, i) => ({
    key: `${i}`,
    label: p.label,
    values: p.values,
  }));
  const showTotal = chart.kind === "stacked_bar" && chart.series.length > 1;

  const onDownload = () =>
    exportDataToExcel(
      chart.points.map((p) => ({
        "": p.label,
        ...Object.fromEntries(chart.series.map((s, i) => [s, p.values[i]])),
        ...(showTotal
          ? { Total: p.values.reduce((s, v) => s + (v || 0), 0) }
          : {}),
      })),
      chart.title || "ProCharted answer",
    );

  return (
    <div
      className={"rounded-[12px] border border-[#EDEFF3] bg-white p-3"}
      data-testid={"ask-chart"}>
      <div className={"mb-2.5 flex flex-wrap items-center gap-2"}>
        {chart.title && (
          <span className={"text-[13px] font-extrabold text-[#1F2433]"}>
            {chart.title}
          </span>
        )}
        <div className={"ml-auto flex items-center gap-3"}>
          <ChartLegend series={chart.series} />
          <div
            className={"inline-flex rounded-[9px] bg-[#F2F4F7] p-[3px]"}
            role={"group"}
            aria-label={"Chart or table"}>
            {(["chart", "table"] as const).map((v) => (
              <button
                key={v}
                type={"button"}
                aria-pressed={view === v}
                onClick={() => setView(v)}
                className={classNames(
                  "rounded-[7px] px-2.5 py-1 text-[12px] font-bold",
                  view === v
                    ? "bg-white text-[#1F2433] shadow-[0_1px_2px_rgba(0,0,0,0.08)]"
                    : "text-[#6B7185]",
                )}>
                {v === "chart" ? "Chart" : "Table"}
              </button>
            ))}
          </div>
        </div>
      </div>
      {view === "chart" ? (
        <ReportChart
          kind={chart.kind}
          series={chart.series}
          points={points}
          format={format}
          horizontal={chart.kind !== "line"}
        />
      ) : (
        <div className={"max-h-[260px] overflow-auto"}>
          <table className={"w-full border-collapse text-[12.5px]"}>
            <thead>
              <tr>
                <th
                  className={
                    "border-b border-[#EDEFF3] px-2 py-1.5 text-left text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
                  }
                />
                {chart.series.map((s) => (
                  <th
                    key={s}
                    className={
                      "border-b border-[#EDEFF3] px-2 py-1.5 text-right text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
                    }>
                    {s}
                  </th>
                ))}
                {showTotal && (
                  <th
                    className={
                      "border-b border-[#EDEFF3] px-2 py-1.5 text-right text-[10.5px] font-bold uppercase tracking-[0.04em] text-[#9097A8]"
                    }>
                    Total
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {chart.points.map((p, i) => (
                <tr key={i}>
                  <td
                    className={
                      "border-b border-[#EDEFF3] px-2 py-1.5 font-semibold text-[#1F2433]"
                    }>
                    {p.label}
                  </td>
                  {p.values.map((v, j) => (
                    <td
                      key={j}
                      className={
                        "border-b border-[#EDEFF3] px-2 py-1.5 text-right text-[#3A4256]"
                      }>
                      {format(v)}
                    </td>
                  ))}
                  {showTotal && (
                    <td
                      className={
                        "border-b border-[#EDEFF3] px-2 py-1.5 text-right font-bold text-[#1F2433]"
                      }>
                      {format(p.values.reduce((s, v) => s + (v || 0), 0))}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button
        type={"button"}
        onClick={onDownload}
        className={
          "mt-2.5 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-[#6B4EFF] hover:underline"
        }>
        <LuDownload size={14} />
        Download as Excel
      </button>
    </div>
  );
};
